import HotspotButton from '@atoms/HotspotButton/HotspotButton';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import Video from '@atoms/Video/Video';
import { IHotspotCard } from '@models/IHotspotCard';
import { IHotspots } from '@models/IHotspots';
import { IWishlistDrawerLabels } from '@models/IWishlistDrawer';
import CardTypeEnum from '@models/enums/CardTypeEnum';
import AddToWishlist from '@molecules/AddToWishlist/AddToWishlist';
import useMediaQuery from '@utility/hooks/useMediaQuery';
import classNames from 'classnames';
import { useMemo, useState } from 'react';

export const enum HotspotCardVariant {
  shortNoTitle = 'short-no-title',
  tallWithTab = 'tall-with-title',
}

export interface IHotpostCardExtended extends IHotspotCard {
  readonly link?: string;
  readonly title?: string;
  readonly variant?: HotspotCardVariant;
  readonly wishlistDrawerLabels?: IWishlistDrawerLabels;
  readonly showLikeButton?: boolean;
  readonly wishlistId?: number;
  readonly hideMobileHotspots?: boolean;
  readonly linkText?: string;
}

const HotspotCard = (props: IHotpostCardExtended) => {
  const {
    aspectRatio,
    desktopImage,
    mobileImage,
    hotspots,
    link,
    showLikeButton = true,
    variant = HotspotCardVariant.shortNoTitle,
    video,
    wishlistDrawerLabels,
    wishlistId,
    hideMobileHotspots,
    linkText,
  } = props;

  const [activeTooltipIndex, setActiveTooltipIndex] = useState(-1);
  const [videoPlaying, setVideoPlaying] = useState(true);
  const hasVideo = !!video?.videoSource;
  const isMobile = useMediaQuery(752);
  const isPortrait = aspectRatio ? aspectRatio <= 1 : true;
  const calculatedAspectRatio =
    variant === HotspotCardVariant.shortNoTitle ? 4 / 5 : 2 / 3;

  const image = isPortrait ? mobileImage : desktopImage;

  const renderHotspotButton = (hotspot: IHotspots, index: number) => {
    const {
      colour,
      horizontalPercentMobile,
      product,
      verticalPercentMobile,
      horizontalPercentDesktop,
      verticalPercentDesktop,
    } = hotspot ?? {};

    const horizontalPercentage = isPortrait
      ? horizontalPercentMobile
      : horizontalPercentDesktop;

    const verticalPercentage = isPortrait
      ? verticalPercentMobile
      : verticalPercentDesktop;

    const positionStyle = hasVideo
      ? { bottom: 0, right: 0 }
      : {
          left: `${horizontalPercentage}%`,
          top: `${verticalPercentage}%`,
        };

    return (
      <div
        key={`${product?.name}-${horizontalPercentage}-${verticalPercentage}`}
        className="tw-absolute tw-z-10 -tw-translate-x-1/2 -tw-translate-y-1/2"
        style={positionStyle}
      >
        <HotspotButton
          activeTooltipIndex={activeTooltipIndex}
          colourHexCode={colour?.hexCode}
          colourName={colour?.name}
          colourUrl={colour?.url}
          colourPreviewUrl={colour?.previewUrl}
          colourCode={colour?.colourCode}
          index={index}
          onToggleTooltip={() =>
            setActiveTooltipIndex((prevIndex) =>
              prevIndex === index ? -1 : index,
            )
          }
          productName={product?.name}
          productUrl={product?.url}
          tooltipShouldStack={hasVideo}
          hotspots={hotspots}
          tooltipPositionedUnderHotspot={verticalPercentage < 50 && !hasVideo}
          tooltipOverflowLeft={horizontalPercentage < 35 && !hasVideo}
          tooltipOverflowRight={horizontalPercentage > 65 || hasVideo}
        />
      </div>
    );
  };

  const getHotspotsToRender = () => {
    if (!hotspots?.length) return [];

    if (hasVideo) {
      // Render only the first hotspot if a video is present
      return [renderHotspotButton(hotspots[0]!, 0)];
    } else {
      // Render all hotspots otherwise
      return hotspots.map(renderHotspotButton);
    }
  };

  const renderHotspots = useMemo(getHotspotsToRender, [
    hotspots,
    hasVideo,
    activeTooltipIndex,
    isPortrait,
  ]);

  const isLink = isMobile && hideMobileHotspots;
  const Wrapper = isLink ? 'a' : 'div';

  return (
    <Wrapper
      className={classNames(
        'tw-h-full tw-text-black',
        variant === HotspotCardVariant.tallWithTab &&
          'tw-pb-[60px] md:tw-pb-[76px]',
        !aspectRatio && 'tw-bg-white',
        isLink && 'focus:tw-outline-grey-100',
      )}
      href={isLink ? link : undefined}
      aria-label={isLink ? linkText : undefined}
    >
      <div
        className={classNames('tw-relative tw-h-auto tw-overflow-hidden')}
        style={{ aspectRatio: aspectRatio || calculatedAspectRatio }}
      >
        {showLikeButton && (
          <AddToWishlist
            wishlistId={wishlistId}
            card={{ ...props, aspectRatio: undefined } as IHotspotCard}
            cardId={`${link || hotspots[0]?.product?.url}.html` || ''} // To match existing format
            collection={CardTypeEnum.Inspirations}
            wishlistDrawerLabels={wishlistDrawerLabels}
          />
        )}
        <div className={hideMobileHotspots ? 'max-md:tw-hidden' : undefined}>
          {!!hotspots.length && renderHotspots}
        </div>
        {image && (
          <Image
            imageClasses="tw-w-full"
            image={image}
            sizes="50vw"
            aspectRatio={aspectRatio || calculatedAspectRatio}
          />
        )}
        {hasVideo && (
          <div className="tw-relative tw-h-full">
            <button
              className="tw-absolute tw-right-0 tw-top-0 tw-z-20 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-bg-white/40"
              onClick={() => setVideoPlaying(!videoPlaying)}
              aria-label={videoPlaying ? 'Pause video' : 'Play video'}
            >
              <Icon
                name={videoPlaying ? 'pause' : 'play'}
                className="!tw-h-auto !tw-w-2"
              />
            </button>

            <Video
              videoSource={video.videoSource}
              videoAttributes={{
                playsinline: true,
                muted: true,
                playing: videoPlaying,
                loop: true,
              }}
              controls={false}
              classes="tw-w-full tw-h-full [&_video]:tw-object-center [&_video]:tw-object-cover tw-inset-0"
            />
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default HotspotCard;
